/**
 * RDV en ligne (#429) — Story 3.4 (#553). Email-first matching contract.
 *
 * The public booking flow starts with the client's email. The server recognizes
 * the client WITHOUT leaking any PII: it returns only the SHAPE of the tunnel —
 * which steps remain to fill — never the stored fiche, name, phone or address
 * (FR21a/b/c). The screen shown at email entry is identical whatever the status,
 * so enumeration is bounded only by rate-limiting (NFR3, posture A).
 *
 * The matching rule (single result = recognized) and the "exploitable address"
 * predicate live here as a PURE function so they can be exhaustively unit-tested
 * without a NestJS / DB harness. The route resolves the company from the slug,
 * loads the matching customers, then calls `resolveTunnelShape`.
 */
import { z } from "zod";
import { isInvalid } from "../customer/services.js";
import { PUBLIC_API_EMAIL_MAX } from "../publicApi/bounds.js";
/** Email-first request payload — the only thing the browser sends (FR21a). Anti-abuse bound: `publicApi/bounds.ts` (#622). */
export const MatchEmailPayloadSchema = z.object({
    email: z.string().email().max(PUBLIC_API_EMAIL_MAX),
});
/**
 * Resolve the tunnel shape from the customers whose email matched (already
 * filtered by the route: same company, case-insensitive email, not deleted, not
 * archived).
 *
 * Email is NON-unique in base, so recognition is intentionally strict:
 * - **exactly 1** match → recognized;
 * - **0 or >1** → not recognized (an ambiguous match must not skip a step) →
 *   `"full"`.
 *
 * When recognized, the stored address decides what remains:
 * - **exploitable** (`!isInvalid`: coordinates present and a granularity that
 *   is not `municipality` — the score no longer gates this, #797)
 *   → `"none"`: the engine will read the stored address.
 * - **inexploitable** → `"addressOnly"`: re-ask the address (to obtain a findable
 *   one) but keep the recognized contact.
 */
export function resolveTunnelShape(matches) {
    if (matches.length !== 1) {
        return "full";
    }
    const customer = matches[0];
    if (customer == null) {
        return "full";
    }
    return isInvalid(customer) ? "addressOnly" : "none";
}
//# sourceMappingURL=matchEmail.js.map